import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router';
import { Card, Button, Space, Spin, Alert, Empty, Typography, Breadcrumb } from 'antd';
import { ArrowLeftOutlined, PlusOutlined, HomeOutlined, AuditOutlined, TeamOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { contactService } from '../../services/api/contactService';
import type { ProducerContact } from '../../types/contact';
import { ContactList } from './components/ContactList';
import { CreateContactModal } from './components/CreateContactModal';
import { EditContactModal } from './components/EditContactModal';
import { DeleteContactModal } from './components/DeleteContactModal';

const { Title } = Typography;

interface ProducerContactsViewProps {
  producerId?: number;
  auditId?: number;
}

/**
 * ProducerContactsView Component
 * 
 * Displays and manages contacts for a producer.
 * Can be accessed either directly via producer ID or through an audit context.
 * 
 * Features:
 * - Lists all active contacts for a producer
 * - Allows adding new contacts
 * - Supports editing and deleting contacts
 * - Toggle favorite status
 * - Displays contact information with visual indicators for main/favorite contacts
 */
export const ProducerContactsView: React.FC<ProducerContactsViewProps> = ({
  producerId: propProducerId,
  auditId: propAuditId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ producerId?: string; auditId?: string }>();

  // Get IDs from props or URL params
  const producerId = propProducerId || (params.producerId ? parseInt(params.producerId) : undefined);
  const auditId = propAuditId || (params.auditId ? parseInt(params.auditId) : undefined);

  // State management
  const [contacts, setContacts] = useState<ProducerContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [producerName, setProducerName] = useState<string>('');
  const [auditReference, setAuditReference] = useState<string>('');
  const [actualProducerId, setActualProducerId] = useState<number | undefined>(producerId);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedContact, setSelectedContact] = useState<ProducerContact | null>(null);

  /**
   * Load contacts from API
   * Uses either audit-based or producer-based endpoint depending on context
   */
  const loadContacts = async () => {
    if (!producerId && !auditId) {
      setError(t('contacts.error.no_id') || 'No se proporcionó ID de productor o auditoría');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let response;
      
      if (auditId) {
        // Load contacts via audit endpoint
        response = await contactService.getAuditProducerContacts(auditId);
      } else if (producerId) {
        // Load contacts via producer endpoint
        response = await contactService.getProducerContacts(producerId);
      }

      if (response) {
        setContacts(response.results);
        
        // Extract producer ID from response (when coming from audit) or from first contact
        if (!actualProducerId) {
          if (response.producer_id) {
            // Producer ID provided directly in response (from audit endpoint)
            setActualProducerId(response.producer_id);
          } else if (response.results.length > 0 && response.results[0].producer) {
            // Extract from first contact as fallback
            setActualProducerId(response.results[0].producer);
          }
        }
        
        // Set producer name if we have contacts
        if (response.results.length > 0 && response.results[0].producer_name) {
          setProducerName(response.results[0].producer_name);
        }
      }
    } catch (err: any) {
      console.error('Error loading contacts:', err);
      setError(err.message || t('contacts.error.load') || 'Error al cargar contactos');
    } finally {
      setLoading(false);
    }
  };

  // Load contacts on component mount or when IDs change
  useEffect(() => {
    loadContacts();
  }, [producerId, auditId]);

  // Fetch audit reference for breadcrumb
  useEffect(() => {
    const fetchAuditReference = async () => {
      if (!auditId) return;
      
      try {
        const { apiClient } = await import('../../services/api/apiClient');
        const response = await apiClient.get(`/audits/${auditId}/`);
        setAuditReference(response.data.reference || `${auditId}`);
      } catch (error) {
        console.error('Error fetching audit reference:', error);
        setAuditReference(`${auditId}`);
      }
    };

    fetchAuditReference();
  }, [auditId]);

  /**
   * Handle back navigation
   * Returns to previous page or default location
   */
  const handleBack = () => {
    const from = location.state?.from;
    if (from) {
      navigate(from);
    } else if (auditId) {
      navigate(`/audits/${auditId}`);
    } else if (producerId) {
      navigate(`/producers/${producerId}`);
    } else {
      navigate(-1);
    }
  };

  /**
   * Handle add contact button click
   * Opens modal for creating new contact
   */
  const handleAddContact = () => {
    setCreateModalVisible(true);
  };

  /**
   * Handle successful contact creation
   * Reloads contacts list
   */
  const handleCreateSuccess = () => {
    loadContacts();
  };

  /**
   * Handle edit contact
   * Opens modal for editing contact relationship
   */
  const handleEditContact = (contactId: string) => {
    const contact = contacts.find(c => c.id === contactId);
    if (contact) {
      setSelectedContact(contact);
      setEditModalVisible(true);
    }
  };

  /**
   * Handle successful contact update
   * Reloads contacts list
   */
  const handleEditSuccess = () => {
    loadContacts();
  };

  /**
   * Handle delete contact
   * Opens confirmation modal for deleting contact
   */
  const handleDeleteContact = (contactId: string) => {
    const contact = contacts.find(c => c.id === contactId);
    if (contact) {
      setSelectedContact(contact);
      setDeleteModalVisible(true);
    }
  };

  /**
   * Handle successful contact deletion
   * Reloads contacts list
   */
  const handleDeleteSuccess = () => {
    loadContacts();
  };

  /**
   * Handle toggle favorite status
   * Optimistically updates UI and calls API
   * 
   * Business Rules:
   * 1. Only one contact can be favorite per producer
   * 2. When marking a contact as favorite, automatically unmarks others
   * 3. Cannot unmark favorite if it's the only active contact
   */
  const handleToggleFavorite = async (contactId: string) => {
    const targetContact = contacts.find(c => c.id === contactId);
    if (!targetContact) return;
    
    // Check if trying to unmark the only contact
    if (targetContact.is_favorite && contacts.length === 1) {
      setError(t('contacts.error.cannot_unmark_only_favorite') || 'No se puede desmarcar el favorito si es el único contacto');
      return;
    }
    
    try {
      // Optimistic update with unique favorite logic
      setContacts(prevContacts => {
        const newFavoriteStatus = !targetContact.is_favorite;
        
        return prevContacts.map(contact => {
          if (contact.id === contactId) {
            // Toggle the clicked contact
            return { ...contact, is_favorite: newFavoriteStatus };
          } else if (newFavoriteStatus && contact.is_favorite) {
            // If marking as favorite, unmark other favorites
            return { ...contact, is_favorite: false };
          }
          return contact;
        });
      });

      // Call API
      await contactService.toggleFavorite(contactId);
      
      // Reload to ensure consistency
      await loadContacts();
    } catch (err: any) {
      console.error('Error toggling favorite:', err);
      // Revert optimistic update on error
      await loadContacts();
      setError(err.message || t('contacts.error.toggle_favorite') || 'Error al cambiar favorito');
    }
  };

  /**
   * Render loading state
   */
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <Spin size="large" tip={t('common.loading') || 'Cargando...'} />
      </div>
    );
  }

  /**
   * Render error state
   */
  if (error) {
    return (
      <Card>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
          >
            {t('common.back') || 'Volver'}
          </Button>
          <Alert
            message={t('common.error') || 'Error'}
            description={error}
            type="error"
            showIcon
          />
        </Space>
      </Card>
    );
  }

  // Breadcrumb navigation
  const breadcrumb = (
    <Breadcrumb
      items={[
        {
          title: (
            <Link to="/">
              <HomeOutlined /> {t('home.title') || 'Inicio'}
            </Link>
          ),
        },
        ...(auditId
          ? [
              {
                title: (
                  <Link to="/audits">
                    <AuditOutlined /> {t('audit.title') || 'Auditorías'}
                  </Link>
                ),
              },
              {
                title: <Link to={`/audits/${auditId}`}>{auditReference || `Auditoría ${auditId}`}</Link>,
              },
            ]
          : []),
        {
          title: (
            <>
              <TeamOutlined /> {t('contacts.title') || 'Contactos del Productor'}
            </>
          ),
        },
      ]}
    />
  );

  return (
    <div style={{ padding: '24px' }}>
      {/* Breadcrumb */}
      <div style={{ marginBottom: 16 }}>
        {breadcrumb}
      </div>

      <Card
        title={
          <Title level={3} style={{ margin: 0 }}>
            {producerName 
              ? `${t('contacts.title') || 'Contactos del Productor'} - ${producerName}`
              : t('contacts.title') || 'Contactos del Productor'
            }
          </Title>
        }
        extra={
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
            >
              {t('common.back') || 'Volver'}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddContact}
              disabled={!actualProducerId}
              title={!actualProducerId ? (t('contacts.error.no_producer_id') || 'No se puede agregar contacto sin ID de productor') : undefined}
            >
              {t('contacts.add') || 'Agregar Contacto'}
            </Button>
          </Space>
        }
      >
        {contacts.length === 0 ? (
          <Empty
            description={
              t('contacts.empty') || 'No hay contactos asociados a este productor'
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddContact}
              disabled={!actualProducerId}
              title={!actualProducerId ? (t('contacts.error.no_producer_id') || 'No se puede agregar contacto sin ID de productor') : undefined}
            >
              {t('contacts.add_first') || 'Agregar Primer Contacto'}
            </Button>
          </Empty>
        ) : (
          <ContactList
            contacts={contacts}
            onEdit={handleEditContact}
            onDelete={handleDeleteContact}
            onToggleFavorite={handleToggleFavorite}
            onRefresh={loadContacts}
          />
        )}
      </Card>

      {/* Create Contact Modal */}
      {actualProducerId && (
        <CreateContactModal
          visible={createModalVisible}
          producerId={actualProducerId}
          onClose={() => setCreateModalVisible(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {/* Edit Contact Modal */}
      {selectedContact && (
        <EditContactModal
          visible={editModalVisible}
          contact={selectedContact}
          onClose={() => {
            setEditModalVisible(false);
            setSelectedContact(null);
          }}
          onSuccess={handleEditSuccess}
          totalContacts={contacts.length}
        />
      )}

      {/* Delete Contact Modal */}
      <DeleteContactModal
        visible={deleteModalVisible}
        contact={selectedContact}
        onClose={() => {
          setDeleteModalVisible(false);
          setSelectedContact(null);
        }}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  );
};

export default ProducerContactsView;
